import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  Row,
  Col,
  DatePicker,
  Select,
  Space,
  Table,
  Upload,
  App,
  Spin,
  Tag,
  Modal,
  Popconfirm,
  InputNumber,
  Descriptions,
  Alert,
  Typography
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  FilePdfOutlined,
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
  DownloadOutlined,
  FileImageOutlined,
  FileTextOutlined,
  CheckOutlined,
  InboxOutlined,
  CloseOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { authService } from '../services/auth';
import { normalizePZDocument, PZDocumentDto } from '../dto/pz';
import { canManagePZDocuments } from '../modules/warehouse/warehouseDocumentAccessPolicy';
import { buildPZItemsPayload, validatePZItemsForSave } from '../modules/warehouse/pzItemPayload';
import { buildPZVariantAutofill } from '../modules/warehouse/pzVariantSelection';
import dayjs from 'dayjs';
import { formatDate } from '../utils/dateUtils';
import { formatCurrency } from '../utils/currency';
import { formatUnit } from '../utils/units';
import { calculatePZDocumentTotal } from '../utils/pzTotals';
import { PZItem, PZAttachment, PZStatus, CreatePZDto, UpdatePZDto, Supplier, ProductVariant } from '../types/pz';
import { getAuthFileUrl } from '../utils/fileUrl';
import { getApiErrorMessage } from '../utils/apiError';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../utils/polishNumberInput';
import {
  createSupplierOrderPZ,
  fetchSupplierOrderPZCandidates,
} from '../modules/orders/api/supplierOrdersApi';
import type { SupplierOrderDto } from '../dto/supplierOrder';
import SupplierOrderProductsPreview from '../modules/orders/components/SupplierOrderProductsPreview';
import SupplierOrderSummaryPreview from '../modules/orders/components/SupplierOrderSummaryPreview';
import PZItemsSection, { type PZItemRow } from './PZItemsSection';
import './PZDetail.css';

const { Option } = Select;
const { TextArea } = Input;

type NewPZMode = 'manual' | 'supplierOrder';

interface PZInventoryProcessingSummary {
  batchesCreated: number;
  totalQuantity: number;
  totalCostPLN?: number;
}

const getPZErrorMessage = (error: any, t: any, fallbackKey: string) => {
  if (error?.response?.data?.code === 'PZ_NUMBER_EXISTS') {
    return t('pz:error.pzNumberExists');
  }
  if (error?.response?.data?.code === 'PZ_UNIT_PRICE_REQUIRED') {
    return t('pz:validation.priceRequiredBeforeStatus');
  }

  return getApiErrorMessage(error, t, fallbackKey);
};

const PZDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['pz', 'common', 'orders']);
  const { message } = App.useApp();
  const [form] = Form.useForm();

  const isNewDocument = id === 'new';
  const queryParams = new URLSearchParams(location.search);
  const isEditMode = queryParams.get('mode') === 'edit' || isNewDocument;
  const initialNewPZMode = (
    queryParams.get('creationMode') === 'manual'
      ? 'manual'
      : queryParams.get('creationMode') === 'supplierOrder'
        ? 'supplierOrder'
        : 'manual'
  ) as NewPZMode;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newPZMode, setNewPZMode] = useState<NewPZMode>(() => (isNewDocument ? initialNewPZMode : 'manual'));
  const [supplierOrders, setSupplierOrders] = useState<SupplierOrderDto[]>([]);
  const [supplierOrdersLoading, setSupplierOrdersLoading] = useState(false);
  const [supplierOrderSearch, setSupplierOrderSearch] = useState('');
  const [generatingSupplierOrderId, setGeneratingSupplierOrderId] = useState<string | null>(null);
  const [pzDocument, setPzDocument] = useState<PZDocumentDto | null>(null);
  const [attachments, setAttachments] = useState<PZAttachment[]>([]);
  const [items, setItems] = useState<PZItemRow[]>([
    { key: '0', productVariantId: '', quantityOrdered: 1, unitPrice: undefined, currency: 'PLN' }
  ]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewContent, setPreviewContent] = useState<{ type: string; url: string } | null>(null);
  const [inventoryInfo, setInventoryInfo] = useState<PZInventoryProcessingSummary | null>(null);
  const [itemValidationError, setItemValidationError] = useState<string | null>(null);
  const [statusChanging, setStatusChanging] = useState(false);
  const statusChangeInFlightRef = useRef(false);

  const currentUser = authService.getCurrentUser();
  const canEdit = canManagePZDocuments(currentUser);
  const canViewPZPurchasePrices = authService.canViewWarehouseDocumentPurchasePrices();
  const canEditPurchasePrices = authService.canEditPZDocumentPurchasePrices();
  const canViewPurchaseDocuments = authService.canViewPurchaseDocuments();
  const currentUserDisplayName = currentUser?.name || '';

  const fetchPZDocument = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/pz/${id}`);
      const doc = normalizePZDocument(response.data);
      setPzDocument(doc);
      const formValues: Record<string, unknown> = { ...doc };
      [
        'sourceTransportId',
        'sourceTransportIds',
        'sourceTransports',
        'transportCompany',
        'transportInvoiceNo',
        'transportCost',
        'transportCurrency',
        'vehicleRegistration',
        'driverName',
        'driverPhone',
        'allocateTransportCost',
      ].forEach((field) => {
        delete formValues[field];
      });

      // Set form values
      form.setFieldsValue({
        ...formValues,
        receiptDate: dayjs(doc.receiptDate),
        issueDate: doc.issueDate ? dayjs(doc.issueDate) : undefined,
        expectedDeliveryDate: doc.expectedDeliveryDate ? dayjs(doc.expectedDeliveryDate) : undefined,
        supplierInvoiceDate: doc.supplierInvoiceDate ? dayjs(doc.supplierInvoiceDate) : undefined,
        supplierId: doc.supplierId,
        approvedBy: doc.approvedBy,
        receivedBy: doc.receivedBy,
      });

      // Set items
      if (doc.itemsLoaded) {
        setItems(doc.items.map((item: PZItem, index: number) => ({
          ...item,
          key: item.id || index.toString()
        })));
      }
    } catch (error: any) {
      message.error(getPZErrorMessage(error, t, 'pz:error.fetchDetailFailed'));
      navigate('/app/pz');
    } finally {
      setLoading(false);
    }
  }, [id, form, navigate, t, message]);

  const fetchAttachments = useCallback(async () => {
    try {
      const response = await api.get(`/pz/${id}/attachments`);
      setAttachments(response.data);
    } catch (error: any) {
      console.error('Error fetching attachments:', error);
    }
  }, [id]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await api.get('/suppliers?isActive=true');
      setSuppliers(response.data);
    } catch (error: any) {
      console.error('Error fetching suppliers:', error);
      message.error(t('pz:error.suppliersFetchFailed'));
    }
  }, [t, message]);

  useEffect(() => {
    fetchSuppliers();
    if (!isNewDocument) {
      fetchPZDocument();
      fetchAttachments();
    } else {
      // Reset any stale values left over from a previously viewed/edited PZ
      // document before starting a new one, so navigating straight from an
      // existing document's edit page into "new" mode never bleeds in the
      // old record's data.
      form.resetFields();
      setPzDocument(null);
      setAttachments([]);
      setItems([{ key: '0', productVariantId: '', quantityOrdered: 1, unitPrice: undefined, currency: 'PLN' }]);
      setInventoryInfo(null);
    }
  }, [isNewDocument, fetchPZDocument, fetchAttachments, fetchSuppliers, form]);

  useEffect(() => {
    if (!isNewDocument) {
      setNewPZMode('manual');
    }
  }, [isNewDocument]);

  useEffect(() => {
    if (!isNewDocument || newPZMode !== 'supplierOrder') {
      return;
    }

    let cancelled = false;
    const search = supplierOrderSearch.trim();
    const loadSupplierOrders = async () => {
      setSupplierOrdersLoading(true);
      try {
        const orders = await fetchSupplierOrderPZCandidates({
          search: search.length >= 3 ? search : undefined,
        });
        if (!cancelled) {
          setSupplierOrders(orders);
        }
      } catch (error) {
        console.error('Error fetching supplier orders for PZ generation:', error);
        if (!cancelled) {
          message.error(t('pz:generateFromSupplierOrder.loadFailed'));
        }
      } finally {
        if (!cancelled) {
          setSupplierOrdersLoading(false);
        }
      }
    };

    const timeout = window.setTimeout(loadSupplierOrders, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [isNewDocument, newPZMode, supplierOrderSearch, message, t]);

  const handleSupplierChange = (supplierId: string) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    if (supplier) {
      form.setFieldsValue({
        supplierName: supplier.name,
        supplierAddress: supplier.address || '',
        supplierNIP: supplier.taxId || '',
      });
    }
  };

  const getSupplierOrderPZEligibleItemCount = (supplierOrder: SupplierOrderDto) => (
    (supplierOrder.items || []).filter((item) => item.pzReceipt?.eligible).length
  );

  const handleCreateFromSupplierOrder = async (supplierOrder: SupplierOrderDto) => {
    setGeneratingSupplierOrderId(supplierOrder.id);
    try {
      const result = await createSupplierOrderPZ(supplierOrder.id);
      message.success(t('pz:generateFromSupplierOrder.created', {
        pzNumber: result.pzNumber,
        count: result.createdItemCount,
      }));
      navigate(`/app/pz/${result.pzDocumentId}?mode=edit`);
    } catch (error: any) {
      message.error(error.response?.data?.message || t('pz:generateFromSupplierOrder.createFailed'));
      setGeneratingSupplierOrderId(null);
    }
  };

  const handleSave = async (values: any) => {
    setSaving(true);
    try {
      if (!isNewDocument && pzDocument && !pzDocument.itemsLoaded) {
        message.error(t('pz:validation.itemsNotLoaded'));
        setSaving(false);
        return;
      }

      const documentCurrency = values.currency || values.supplierInvoiceCurrency || 'PLN';
      const itemValidationKey = validatePZItemsForSave(items, { canEditPurchasePrices });
      if (itemValidationKey) {
        const validationMessage = t(`pz:validation.${itemValidationKey}`);
        setItemValidationError(validationMessage);
        message.error(validationMessage);
        setSaving(false);
        return;
      }
      setItemValidationError(null);
      const itemPayload = buildPZItemsPayload(items, {
        canEditPurchasePrices,
        documentCurrency,
      });

      // Validate items
      if (itemPayload.length === 0) {
        message.error(t('pz:validation.itemsRequired'));
        setSaving(false);
        return;
      }

      const data: CreatePZDto | UpdatePZDto = {
        pzNumber: values.pzNumber?.trim() || undefined,
        receiptDate: values.receiptDate.format('YYYY-MM-DD'),
        supplierId: values.supplierId,
        currency: documentCurrency,
        supplierInvoiceNumber: canEditPurchasePrices ? values.supplierInvoiceNumber : undefined,
        supplierInvoiceDate: canEditPurchasePrices ? values.supplierInvoiceDate?.format('YYYY-MM-DD') : undefined,
        supplierInvoiceAmount: canEditPurchasePrices ? values.supplierInvoiceAmount : undefined,
        supplierInvoiceCurrency: canEditPurchasePrices ? values.supplierInvoiceCurrency || documentCurrency : undefined,
        warehouseLocation: values.warehouseLocation,
        approvedBy: values.approvedBy?.trim() || null,
        receivedBy: values.receivedBy?.trim() || null,
        ...(isNewDocument ? { allocateTransportCost: false } : {}),
        notes: values.notes,
        items: itemPayload,
      };

      if (isNewDocument) {
        await api.post('/pz', data);
        message.success(t('pz:success.created'));
        navigate('/app/pz');
      } else {
        await api.put(`/pz/${id}`, data);
        message.success(t('pz:success.updated'));
        navigate('/app/pz');
      }
    } catch (error: any) {
      message.error(getPZErrorMessage(error, t, 'pz:error.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const getSelectedCurrency = () =>
    form.getFieldValue('currency') || form.getFieldValue('supplierInvoiceCurrency') || 'PLN';

  const syncDocumentCurrency = (currency: string) => {
    const nextCurrency = currency || 'PLN';
    form.setFieldsValue({
      currency: nextCurrency,
      supplierInvoiceCurrency: nextCurrency,
    });
    setItems((currentItems) => currentItems.map((item) => ({
      ...item,
      currency: nextCurrency,
    })));
  };

  const handleFormValuesChange = (changedValues: Record<string, unknown>) => {
    if (typeof changedValues.currency === 'string') {
      syncDocumentCurrency(changedValues.currency);
    } else if (typeof changedValues.supplierInvoiceCurrency === 'string') {
      syncDocumentCurrency(changedValues.supplierInvoiceCurrency);
    }
  };

  const handleStatusChange = async (newStatus: PZStatus) => {
    if (statusChangeInFlightRef.current) return;

    if ([PZStatus.CONFIRMED, PZStatus.RECEIVED].includes(newStatus)) {
      const missingPrice = items.some((item) => {
        const unitPrice = Number(item.unitPrice);
        return !Number.isFinite(unitPrice) || unitPrice <= 0;
      });
      if (items.length === 0 || missingPrice) {
        const validationMessage = t('pz:validation.priceRequiredBeforeStatus');
        setItemValidationError(validationMessage);
        message.error(validationMessage);
        return;
      }
    }

    statusChangeInFlightRef.current = true;
    setStatusChanging(true);
    try {
      const response = await api.put(`/pz/${id}/status`, { status: newStatus });

      if (newStatus === PZStatus.RECEIVED && response.data.inventoryProcessing) {
        setInventoryInfo((current) => {
          const next = response.data.inventoryProcessing as PZInventoryProcessingSummary;
          const nextHasReceipt = Number(next.totalQuantity || 0) > 0 || Number(next.batchesCreated || 0) > 0;
          const currentHasReceipt = Number(current?.totalQuantity || 0) > 0 || Number(current?.batchesCreated || 0) > 0;
          return currentHasReceipt && !nextHasReceipt ? current : next;
        });
        message.success(t('pz:success.received'));
      } else if (newStatus === PZStatus.CONFIRMED) {
        message.success(t('pz:success.confirmed'));
      } else if (newStatus === PZStatus.CANCELLED) {
        message.success(t('pz:success.cancelled'));
      }

      await fetchPZDocument();
    } catch (error: any) {
      const errorKey = newStatus === PZStatus.RECEIVED ? 'receiveFailed' :
                       newStatus === PZStatus.CONFIRMED ? 'confirmFailed' :
                       newStatus === PZStatus.CANCELLED ? 'cancelFailed' : 'saveFailed';
      message.error(getPZErrorMessage(error, t, `pz:error.${errorKey}`));
    } finally {
      statusChangeInFlightRef.current = false;
      setStatusChanging(false);
    }
  };

  const handleUpload = async (options: any) => {
    const { file, onSuccess, onError } = options;
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`/pz/${id}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      onSuccess(response.data);
      message.success(t('pz:success.fileUploaded'));
      fetchAttachments();
    } catch (error: any) {
      onError(error);
      message.error(getPZErrorMessage(error, t, 'pz:error.uploadFailed'));
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    try {
      await api.delete(`/pz/attachments/${attachmentId}`);
      message.success(t('pz:success.attachmentDeleted'));
      fetchAttachments();
    } catch (error: any) {
      message.error(getPZErrorMessage(error, t, 'pz:error.deleteAttachmentFailed'));
    }
  };

  const handleDownloadPDF = async () => {
    if (!canViewPZPurchasePrices) {
      return;
    }

    try {
      const response = await api.get(`/pz/${id}/pdf`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${pzDocument?.pzNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success(t('pz:success.pdfDownloaded'));
    } catch (error: any) {
      message.error(getPZErrorMessage(error, t, 'pz:error.pdfGenerateFailed'));
    }
  };

  const handlePreviewAttachment = async (attachment: PZAttachment) => {
    if (attachment.fileType.startsWith('image/')) {
      setPreviewContent({
        type: 'image',
        url: getAuthFileUrl(`/api/uploads/${attachment.filePath}`)
      });
      setPreviewVisible(true);
    } else if (attachment.fileType === 'application/pdf') {
      setPreviewContent({
        type: 'pdf',
        url: getAuthFileUrl(`/api/uploads/${attachment.filePath}`)
      });
      setPreviewVisible(true);
    } else {
      // For other file types, download directly
      const link = document.createElement('a');
      link.href = getAuthFileUrl(`/api/uploads/${attachment.filePath}`);
      link.download = attachment.fileName;
      link.click();
    }
  };

  const addItem = () => {
    setItems([...items, {
      key: Date.now().toString(),
      productVariantId: '',
      quantityOrdered: 1,
      unitPrice: undefined,
      currency: getSelectedCurrency()
    }]);
  };

  const removeItem = (key: string) => {
    setItems(items.filter(item => item.key !== key));
  };

  const updateItem = (key: string, field: keyof PZItemRow, value: any) => {
    setItemValidationError(null);
    const updatedItems = items.map(item => {
      if (item.key === key) {
        const updated = { ...item, [field]: value };

        // Calculate total price
        if (canEditPurchasePrices && (field === 'quantityOrdered' || field === 'unitPrice')) {
          updated.totalPrice = (updated.quantityOrdered || 0) * (updated.unitPrice || 0);
        }

        return updated;
      }
      return item;
    });
    setItems(updatedItems);
  };

  // Auto-fill product info from the catalog item supplied by VariantPicker.
  // Reads the passed `variant` (not a separate fetch) so it also works for a
  // product created inline via the picker's "Add new product" flow.
  const handleVariantSelect = (key: string, variantId: string | undefined, variant?: ProductVariant) => {
    const updatedItems = items.map(item => {
      if (item.key !== key) return item;
      return {
        ...item,
        ...buildPZVariantAutofill(item, variantId, variant, canEditPurchasePrices),
      };
    });
    setItems(updatedItems);
  };

  const getStatusColor = (status: PZStatus) => {
    switch (status) {
      case PZStatus.DRAFT:
        return 'default';
      case PZStatus.CONFIRMED:
        return 'processing';
      case PZStatus.RECEIVED:
        return 'success';
      case PZStatus.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  };

  const attachmentColumns = [
    {
      title: t('pz:fields.fileName'),
      dataIndex: 'fileName',
      key: 'fileName',
      render: (text: string, record: PZAttachment) => (
        <Space>
          {record.fileType.startsWith('image/') ? (
            <FileImageOutlined />
          ) : record.fileType === 'application/pdf' ? (
            <FilePdfOutlined />
          ) : (
            <FileTextOutlined />
          )}
          <Button type="link" onClick={() => handlePreviewAttachment(record)}>{text}</Button>
        </Space>
      ),
    },
    {
      title: t('pz:fields.fileSize'),
      dataIndex: 'fileSize',
      key: 'fileSize',
      width: 120,
      render: (size: number) => `${(size / 1024).toFixed(2)} KB`,
    },
    {
      title: t('pz:fields.uploadedAt'),
      dataIndex: 'uploadedAt',
      key: 'uploadedAt',
      width: 150,
      render: (date: string) => formatDate(date),
    },
    {
      title: t('common:actions'),
      key: 'actions',
      width: 120,
      render: (record: PZAttachment) => (
        <Space>
          <Button
            icon={<DownloadOutlined />}
            size="small"
            onClick={() => handlePreviewAttachment(record)}
          />
          {canEdit && (
            <Popconfirm
              title={t('pz:confirm.deleteAttachment')}
              onConfirm={() => { void handleDeleteAttachment(record.id); }}
              okText={t('common:yes')}
              cancelText={t('common:no')}
            >
              <Button danger icon={<DeleteOutlined />} size="small" />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Calculate totals
  const calculateTotals = () => {
    const totalQuantity = items.reduce((sum, item) => sum + (item.quantityOrdered || 0), 0);
    const totalValue = canViewPZPurchasePrices
      ? calculatePZDocumentTotal(items, form.getFieldValue('currency') || 'PLN')
      : { amount: 0, currency: form.getFieldValue('currency') || 'PLN', mixedCurrency: false };
    return { totalQuantity, totalValue };
  };


  const { totalQuantity, totalValue } = calculateTotals();

  const renderStatusActions = () => {
    if (isNewDocument || isEditMode || !pzDocument || !canEdit) {
      return null;
    }

    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Alert
          message={t(`pz:statusFlow.${pzDocument.status.toLowerCase()}Info`)}
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
        />
        <Space wrap>
          {pzDocument.status === PZStatus.DRAFT && (
            <Popconfirm
              title={t('pz:confirm.confirmPZ')}
              onConfirm={() => { void handleStatusChange(PZStatus.CONFIRMED); }}
              okText={t('common:yes')}
              cancelText={t('common:no')}
            >
              <Button type="primary" icon={<CheckOutlined />} loading={statusChanging} disabled={statusChanging}>
                {t('pz:actions.confirm')}
              </Button>
            </Popconfirm>
          )}
          {pzDocument.status === PZStatus.CONFIRMED && (
            <Popconfirm
              title={t('pz:confirm.receivePZ')}
              onConfirm={() => { void handleStatusChange(PZStatus.RECEIVED); }}
              okText={t('common:yes')}
              cancelText={t('common:no')}
            >
              <Button type="primary" icon={<InboxOutlined />} loading={statusChanging} disabled={statusChanging}>
                {t('pz:actions.receive')}
              </Button>
            </Popconfirm>
          )}
          {(pzDocument.status === PZStatus.DRAFT || pzDocument.status === PZStatus.CONFIRMED) && (
            <Popconfirm
              title={t('pz:confirm.cancelPZ')}
              onConfirm={() => { void handleStatusChange(PZStatus.CANCELLED); }}
              okText={t('common:yes')}
              cancelText={t('common:no')}
            >
              <Button danger icon={<CloseOutlined />} loading={statusChanging} disabled={statusChanging}>
                {t('pz:actions.cancel')}
              </Button>
            </Popconfirm>
          )}
        </Space>
      </Space>
    );
  };

  const supplierOrderPickerColumns = [
    {
      title: t('orders:supplierOrders.table.supplier'),
      key: 'supplier',
      width: 260,
      render: (_: unknown, record: SupplierOrderDto) => (
        <SupplierOrderSummaryPreview
          order={record}
          onOrderClick={() => navigate(`/app/supplier-orders/${record.id}`)}
        />
      ),
    },
    {
      title: t('pz:generateFromSupplierOrder.goods'),
      key: 'products',
      width: 420,
      render: (_: unknown, record: SupplierOrderDto) => (
        <SupplierOrderProductsPreview items={record.items} />
      ),
    },
    {
      title: t('pz:generateFromSupplierOrder.eligibleItems'),
      key: 'eligibleItems',
      width: 110,
      align: 'center' as const,
      render: (_: unknown, record: SupplierOrderDto) => (
        <span className="pz-supplier-order-picker-table__eligible">
          {getSupplierOrderPZEligibleItemCount(record)}
          <span className="pz-supplier-order-picker-table__eligible-total">
            &nbsp;/ {(record.items || []).length}
          </span>
        </span>
      ),
    },
    ...(canViewPZPurchasePrices ? [{
      title: t('pz:fields.totalValue'),
      key: 'totalAmount',
      width: 150,
      align: 'right' as const,
      render: (_: unknown, record: SupplierOrderDto) => (
        <span className="pz-supplier-order-picker-table__value">
          {formatCurrency(record.totalAmount || 0, record.currency || 'PLN')}
        </span>
      ),
    }] : []),
    {
      title: t('common:actions'),
      key: 'actions',
      width: 144,
      align: 'right' as const,
      render: (_: unknown, record: SupplierOrderDto) => {
        const eligibleCount = getSupplierOrderPZEligibleItemCount(record);
        return (
          <Button
            type="primary"
            className="pz-supplier-order-picker-table__action"
            disabled={eligibleCount === 0}
            loading={generatingSupplierOrderId === record.id}
            onClick={() => { void handleCreateFromSupplierOrder(record); }}
          >
            {t('pz:generateFromSupplierOrder.createPZ')}
          </Button>
        );
      },
    },
  ];

  const renderSupplierOrderPZPicker = () => (
    <div style={{ padding: 24, background: '#f6f7f9', minHeight: '100%' }}>
      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <Space align="start" size={12}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/app/pz')}>
            {t('common:back')}
          </Button>
          <Space direction="vertical" size={4} style={{ minWidth: 0 }}>
            <Typography.Text type="secondary">{t('pz:subtitle')}</Typography.Text>
            <Typography.Title level={2} style={{ margin: 0, letterSpacing: 0 }}>
              {t('pz:generateFromSupplierOrder.title')}
            </Typography.Title>
          </Space>
        </Space>

        <Card>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Alert
              type="info"
              showIcon
              message={t('pz:generateFromSupplierOrder.infoTitle')}
              description={t('pz:generateFromSupplierOrder.infoDescription')}
            />
            <Input.Search
              allowClear
              value={supplierOrderSearch}
              onChange={(event) => setSupplierOrderSearch(event.target.value)}
              placeholder={t('pz:generateFromSupplierOrder.searchPlaceholder')}
              style={{ maxWidth: 520 }}
            />
            <Table
              className="pz-supplier-order-picker-table"
              rowKey="id"
              columns={supplierOrderPickerColumns}
              dataSource={supplierOrders}
              loading={supplierOrdersLoading}
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: t('pz:generateFromSupplierOrder.empty') }}
              scroll={{ x: 1080 }}
            />
          </Space>
        </Card>
      </Space>
    </div>
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isNewDocument && newPZMode === 'supplierOrder') {
    return renderSupplierOrderPZPicker();
  }

  return (
    <div className="pz-detail-page" style={{ padding: 24, background: '#f6f7f9', minHeight: '100%' }}>
      <div
        className="pz-detail-header"
        style={{
          marginBottom: 20,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div className="pz-detail-heading">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/app/pz')}>
            {t('common:back')}
          </Button>
          <div className="pz-detail-heading__text">
            <Space wrap>
              {!isNewDocument && pzDocument && pzDocument.status && (
                <Tag color={getStatusColor(pzDocument.status)} style={{ marginInlineEnd: 0 }}>
                  {t(`pz:status.${pzDocument.status.toLowerCase()}`)}
                </Tag>
              )}
              <Typography.Text type="secondary">{t('pz:subtitle')}</Typography.Text>
            </Space>
            <Typography.Title
              level={2}
              style={{
                margin: 0,
                lineHeight: 1.15,
                overflowWrap: 'anywhere',
                letterSpacing: 0,
              }}
            >
              {isNewDocument ? t('pz:actions.create') : pzDocument?.pzNumber}
            </Typography.Title>
          </div>
        </div>
        <div className="pz-detail-actions">
          {!isNewDocument && canViewPZPurchasePrices && (
            <Button icon={<FilePdfOutlined />} onClick={handleDownloadPDF}>
              {t('pz:actions.downloadPDF')}
            </Button>
          )}
          {isEditMode ? (
            <>
              <Button onClick={() => navigate('/app/pz')}>
                {t('common:cancel')}
              </Button>
              <Button type="primary" loading={saving} icon={<SaveOutlined />} onClick={() => form.submit()}>
                {t('common:save')}
              </Button>
            </>
          ) : (
            <>
              {!isNewDocument && canEdit && (
                <Button onClick={() => navigate(`/app/pz/${id}?mode=edit`)}>
                  {t('common:edit')}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Inventory Info Alert after receiving */}
      {inventoryInfo && (
        <Alert
          message={t('pz:inventoryInfo.title')}
          description={
            <Descriptions column={2} size="small">
              <Descriptions.Item label={t('pz:inventoryInfo.batchCreated')}>
                {inventoryInfo.batchesCreated}
              </Descriptions.Item>
              <Descriptions.Item label={t('pz:fields.totalQuantity')}>
                {inventoryInfo.totalQuantity} {formatUnit('PIECE')}
              </Descriptions.Item>
              {canViewPZPurchasePrices && (
                <Descriptions.Item label={t('pz:inventoryInfo.totalCostPLN')}>
                  {formatCurrency(inventoryInfo.totalCostPLN, 'PLN')}
                </Descriptions.Item>
              )}
            </Descriptions>
          }
          type="success"
          showIcon
          icon={<InboxOutlined />}
          closable
          onClose={() => setInventoryInfo(null)}
          style={{ marginBottom: 24 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        onValuesChange={handleFormValuesChange}
        onFinishFailed={() => message.warning(t('pz:fillRequired'))}
        scrollToFirstError
        initialValues={{
          receiptDate: dayjs(),
          currency: 'PLN',
          supplierInvoiceCurrency: 'PLN',
          approvedBy: currentUserDisplayName,
          receivedBy: currentUserDisplayName,
        }}
      >
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
          <Card styles={{ body: { paddingBottom: 8 } }}>
            <Row gutter={[24, 8]}>
              <Col xs={24} xl={10}>
                <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 16 }}>
                  {t('pz:sections.basicInfo')}
                </Typography.Title>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item label={t('pz:fields.pzNumber')} name="pzNumber">
                      <Input
                        disabled={!isEditMode}
                        maxLength={50}
                        placeholder={t('pz:placeholders.pzNumber')}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={t('pz:fields.receiptDate')}
                      name="receiptDate"
                      rules={[{ required: true, message: t('pz:validation.receiptDateRequired') }]}
                    >
                      <DatePicker style={{ width: '100%' }} disabled={!isEditMode} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={t('pz:fields.currency')}
                      name="currency"
                      rules={[{ required: true, message: t('pz:validation.currencyRequired') }]}
                    >
                      <Select disabled={!isEditMode}>
                        <Option value="PLN">PLN</Option>
                        <Option value="USD">USD</Option>
                        <Option value="EUR">EUR</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label={t('pz:fields.warehouseLocation')} name="warehouseLocation">
                      <Input disabled={!isEditMode} />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>

              <Col xs={24} xl={14}>
                <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 16 }}>
                  {t('pz:sections.supplier')}
                </Typography.Title>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={t('pz:fields.supplier')}
                      name="supplierId"
                      rules={[{ required: true, message: t('pz:validation.supplierRequired') }]}
                    >
                      <Select
                        placeholder={t('pz:actions.selectSupplier')}
                        disabled={!isEditMode}
                        onChange={handleSupplierChange}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.children as any)?.toString().toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        {suppliers.map(supplier => (
                          <Option key={supplier.id} value={supplier.id}>
                            {supplier.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label={t('pz:fields.supplierNIP')} name="supplierNIP">
                      <Input disabled={!isEditMode} />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item label={t('pz:fields.supplierAddress')} name="supplierAddress">
                      <Input disabled={!isEditMode} />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>

              {canViewPZPurchasePrices && (
                <Col xs={24}>
                  <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 18 }}>
                    <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 16 }}>
                      {t('pz:sections.supplierInvoice')}
                    </Typography.Title>
                    <Row gutter={16}>
                      <Col xs={24} sm={12} lg={6}>
                        <Form.Item label={t('pz:fields.supplierInvoiceNumber')} name="supplierInvoiceNumber">
                          <Input disabled={!isEditMode} placeholder={t('pz:placeholders.supplierInvoiceNumber')} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <Form.Item label={t('pz:fields.supplierInvoiceDate')} name="supplierInvoiceDate">
                          <DatePicker style={{ width: '100%' }} disabled={!isEditMode} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <Form.Item label={t('pz:fields.supplierInvoiceAmount')} name="supplierInvoiceAmount">
                          <InputNumber
                            style={{ width: '100%' }}
                            disabled={!isEditMode}
                            min={0}
                            step={0.01}
                            placeholder={t('pz:placeholders.supplierInvoiceAmount')}
                            {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <Form.Item label={t('pz:fields.supplierInvoiceCurrency')} name="supplierInvoiceCurrency">
                          <Select disabled={!isEditMode}>
                            <Option value="PLN">PLN</Option>
                            <Option value="USD">USD</Option>
                            <Option value="EUR">EUR</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                </Col>
              )}
            </Row>
          </Card>

          {!isNewDocument && !isEditMode && pzDocument && canEdit && (
            <Card title={t('pz:sections.statusActions')}>
              {renderStatusActions()}
            </Card>
          )}

          <Card
            title={t('pz:sections.items')}
            extra={
              <Space wrap size={12}>
                <Typography.Text type="secondary">
                  {t('pz:fields.totalQuantity')}: <strong>{totalQuantity} {formatUnit('PIECE')}</strong>
                </Typography.Text>
                {canViewPZPurchasePrices && (
                  <Typography.Text type="secondary">
                    {t('pz:fields.totalValue')}: <strong>{formatCurrency(totalValue.amount, totalValue.currency)}</strong>
                  </Typography.Text>
                )}
                {isEditMode && (
                  <Button type="primary" icon={<PlusOutlined />} onClick={addItem}>
                    {t('pz:actions.addItem')}
                  </Button>
                )}
              </Space>
            }
          >
            <PZItemsSection
              items={items}
              isEditMode={isEditMode}
              canViewPurchasePrices={canViewPZPurchasePrices}
              canEditPurchasePrices={canEditPurchasePrices}
              totalQuantity={totalQuantity}
              totalValue={totalValue}
              onItemChange={updateItem}
              onVariantSelect={handleVariantSelect}
              onRemoveItem={removeItem}
            />
            {itemValidationError ? (
              <Alert
                type="error"
                showIcon
                message={itemValidationError}
                style={{ marginTop: 12 }}
              />
            ) : null}
          </Card>

          <Card title={t('pz:sections.signature')}>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t('pz:fields.approvedBy')}
                  name="approvedBy"
                  rules={[{ max: 100, message: t('pz:validation.personNameMax') }]}
                >
                  <Input disabled={!isEditMode} placeholder={t('pz:placeholders.approvedBy')} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t('pz:fields.receivedBy')}
                  name="receivedBy"
                  rules={[{ max: 100, message: t('pz:validation.personNameMax') }]}
                >
                  <Input disabled={!isEditMode} placeholder={t('pz:placeholders.receivedBy')} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <Card title={t('pz:fields.notes')}>
              <Form.Item name="notes" style={{ marginBottom: 0 }}>
                <TextArea rows={5} disabled={!isEditMode} placeholder={t('pz:placeholders.notes')} />
              </Form.Item>
            </Card>

            {!isNewDocument && (
              <Card
                title={t('pz:sections.attachments')}
                extra={
                  canEdit && canViewPurchaseDocuments && (
                    <Upload
                      customRequest={handleUpload}
                      showUploadList={false}
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    >
                      <Button icon={<UploadOutlined />}>
                        {t('pz:actions.uploadFile')}
                      </Button>
                    </Upload>
                  )
                }
              >
                <Table
                  dataSource={attachments}
                  columns={attachmentColumns}
                  pagination={false}
                  rowKey="id"
                  locale={{ emptyText: t('pz:noAttachments') }}
                  scroll={{ x: 520 }}
                />
              </Card>
            )}
          </Space>
        </Space>
      </Form>

      <Modal
        open={previewVisible}
        title={t('pz:actions.documentPreview')}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width={800}
        maskClosable={false}
      >
        {previewContent?.type === 'image' && (
          <img src={previewContent.url} alt={t('pz:actions.documentPreview')} style={{ width: '100%' }} />
        )}
        {previewContent?.type === 'pdf' && (
          <iframe
            src={previewContent.url}
            style={{ width: '100%', height: '600px', border: 'none' }}
            title={t('pz:actions.documentPreview')}
          />
        )}
      </Modal>
    </div>
  );
};

export default PZDetail;
